import axios from 'axios';
import { isNativeApp } from './native';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Stable per-browser id so a known device is not asked for an OTP again.
export function deviceId() {
  try {
    let id = localStorage.getItem('sahakar_device_id');
    if (!id) {
      id = (crypto?.randomUUID?.() || `dev-${Date.now()}-${Math.random().toString(36).slice(2)}`);
      localStorage.setItem('sahakar_device_id', id);
    }
    return id;
  } catch {
    return '';
  }
}

// Web sessions live in HttpOnly cookies. A native shell may need an in-memory
// bearer fallback; it is never persisted. Impersonation stays short-lived/tab-scoped.
let memToken = '';

// Reaching for window.localStorage can itself throw on a locked-down browser, so the
// store is resolved lazily inside the try — never passed in as an argument.
const store = (which) => {
  try { return which === 'session' ? sessionStorage : localStorage; } catch { return null; }
};
const safeGet = (which, key) => { try { return store(which)?.getItem(key) || ''; } catch { return ''; } };
const safeSet = (which, key, value) => {
  try { store(which).setItem(key, value); return true; } catch { return false; }
};
const safeDel = (which, key) => { try { store(which).removeItem(key); } catch { /* blocked */ } };

// Reading window.localStorage itself can throw on a locked-down browser, so every
// access in the app goes through these instead of crashing the page.
export const lsGet = (key) => { try { return localStorage.getItem(key); } catch { return null; } };
export const lsSet = (key, value) => { try { localStorage.setItem(key, value); return true; } catch { return false; } };
export const lsDel = (key) => { try { localStorage.removeItem(key); } catch { /* blocked */ } };

export function authToken() {
  return safeGet('session', 'sahakar_imp_token') || memToken;
}

export function setAuthToken(token) {
  memToken = isNativeApp() ? (token || '') : '';
  safeDel('local', 'sahakar_token');
  safeDel('session', 'sahakar_token');
  return true;
}

export function clearAuthToken() {
  memToken = '';
  safeDel('local', 'sahakar_token');
  safeDel('session', 'sahakar_token');
  clearRefCache();
}

let migration;
export function migrateLegacyAuth() {
  if (safeGet('session', 'sahakar_imp_token')) return Promise.resolve();
  if (migration) return migration;
  const token = safeGet('local', 'sahakar_token') || safeGet('session', 'sahakar_token');
  if (!token) return Promise.resolve();
  migration = api.post('/auth/migrate-session', {}, { headers: { Authorization: `Bearer ${token}` } })
    .finally(() => { safeDel('local', 'sahakar_token'); safeDel('session', 'sahakar_token'); migration = null; });
  return migration;
}

export function storageBlocked() {
  return !safeSet('local', 'sahakar_probe', '1');
}

// Why the last sign-out happened, so the login page can say it instead of showing a
// bare form. Kept in both stores because one of them may be blocked.
let memSignOut = null;

export function noteSignOut(reason) {
  const stamp = String(Date.now());
  safeSet('local', 'sahakar_logout_reason', reason);
  safeSet('local', 'sahakar_logout_reason_ts', stamp);
  safeSet('session', 'sahakar_logout_reason', reason);
  safeSet('session', 'sahakar_logout_reason_ts', stamp);
  memSignOut = { reason, ts: Number(stamp) };
}

export function readSignOut() {
  const reason = safeGet('local', 'sahakar_logout_reason')
    || safeGet('session', 'sahakar_logout_reason') || memSignOut?.reason || '';
  const ts = Number(safeGet('local', 'sahakar_logout_reason_ts')
    || safeGet('session', 'sahakar_logout_reason_ts') || memSignOut?.ts || 0);
  return { reason, ts };
}

// The raw facts of the last auth failure, so a user can read them out to us even if
// the friendly message does not explain enough.
export function noteAuthFailure(status, path, detail) {
  const line = `${new Date().toLocaleTimeString('en-IN')} · ${status || 'no response'} ${path || ''} · ${detail || 'no detail'}`.trim();
  safeSet('local', 'sahakar_auth_detail', line);
  safeSet('session', 'sahakar_auth_detail', line);
  memAuthDetail = line;
}

let memAuthDetail = '';

export function readAuthFailure() {
  return safeGet('local', 'sahakar_auth_detail') || safeGet('session', 'sahakar_auth_detail') || memAuthDetail;
}

export function clearSignOut() {
  memSignOut = null;
  ['sahakar_logout_reason', 'sahakar_logout_reason_ts'].forEach((k) => {
    safeDel('local', k); safeDel('session', k);
  });
}

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
});

// Attach Bearer token if available (fallback for non-cookie environments).
// An "open as user" tab keeps its token in sessionStorage so it stays inside that
// one tab and never disturbs the Super Admin's own session in the others.
api.interceptors.request.use((config) => {
  const token = authToken();
  if (token && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// A session can end from the other side (signed out from another device, account
// disabled, device removed). The backend answers with a specific 401 — log out cleanly.
api.interceptors.response.use(
  (res) => res,
  (error) => {
    const detail = error.response?.data?.detail;
    const ended = typeof detail === 'string'
      && (detail.includes('session has ended') || detail.includes('another device'));
    if (error.response?.status === 401) {
      noteAuthFailure(401, error.config?.url, typeof detail === 'string' ? detail : 'unreadable detail');
    }
    if (error.response?.status === 401 && ended) {
      clearAuthToken();
      noteSignOut(typeof detail === 'string' && detail.includes('another device') ? 'device' : 'ended');
      const p = window.location.pathname;
      if (!p.startsWith('/login') && !p.startsWith('/wa/') && !p.startsWith('/f/')) window.location.assign('/login');
    }
    return Promise.reject(error);
  }
);

// ---- Reference-data cache (entities / vendors / banks / departments) ----
// Navigation used to re-fetch the same look-up lists on every page. These helpers
// paint the cached copy instantly and still refresh from the server in the
// background, so figures stay live while the page stops waiting on the network.
const _refCache = new Map();
const _refInflight = new Map();

export function clearRefCache() {
  _refCache.clear();
  _refInflight.clear();
}

export function swrGet(url, apply, params) {
  const key = params ? `${url}?${JSON.stringify(params)}` : url;
  if (_refCache.has(key) && apply) apply(_refCache.get(key));
  let p = _refInflight.get(key);
  if (!p) {
    p = api.get(url, params ? { params } : undefined)
      .then((r) => { _refCache.set(key, r.data); return r.data; })
      .finally(() => _refInflight.delete(key));
    _refInflight.set(key, p);
  }
  return p.then((data) => { if (apply) apply(data); return { data }; });
}

// Any write can change the look-up lists, so drop the cache after one.
api.interceptors.response.use((res) => {
  if ((res.config?.method || 'get').toLowerCase() !== 'get') clearRefCache();
  return res;
});

export function formatApiError(detail) {
  if (detail == null) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e))).join(' ');
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return String(detail);
}

export function formatINR(n) {
  if (n == null || isNaN(n)) return '₹0';
  const num = Number(n);
  if (Math.abs(num) >= 10000000) return `₹${(num / 10000000).toFixed(2)} Cr`;
  if (Math.abs(num) >= 100000) return `₹${(num / 100000).toFixed(2)} L`;
  if (Math.abs(num) >= 1000) return `₹${(num / 1000).toFixed(1)}K`;
  return `₹${num.toFixed(0)}`;
}

export function formatINRFull(n) {
  if (n == null || isNaN(n)) return '₹0';
  return `₹${Number(n).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
}

export function formatDate(s) {
  if (!s) return '';
  try {
    const d = new Date(s);
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return s;
  }
}
